'use client'

import { useEffect, useRef } from 'react'
import { cubicBezier, ditherClusters, springStep } from '@/lib/dither'

type ToneMap = { width: number; height: number; rows: string[] }

type Particle = {
  element: HTMLSpanElement
  column: number
  row: number
  x: number
  y: number
  target: { x: number; y: number }
  state: { x: number; y: number; vx: number; vy: number }
  hidden: boolean
  entrance: boolean
  entranceStart: { x: number; y: number }
  entranceDelay: number
  entranceDuration: number
}

type ShapeConfig = {
  tone: ToneMap
  className: string
  /** When true, the shape fills its container (width: 100%; height: 100%)
   *  instead of using the tone map's aspect ratio. Use when the tone map
   *  is generated to match a specific container size exactly. */
  fillContainer?: boolean
  /** Scale factor for dot size relative to the grid cell (0–1).
   *  Smaller dots stay hidden within glyph strokes. Defaults to 1. */
  dotScale?: number
  /** When true, dots are invisible at rest and fade in as they're displaced
   *  from their home position. Use for text dither where dots should be
   *  hidden behind the letters until the mouse pushes them out. */
  hidden?: boolean
  /** When true, dots start at random scattered positions and spring to
   *  their home positions on init, creating a flocking entrance animation. */
  entrance?: boolean
}

type DitherRepulsionOptions = {
  shapes: ShapeConfig[]
  radius?: number
  maxPush?: number
  /** CSS selector for an ancestor element to listen for pointer events on.
   *  Defaults to the root element itself. */
  pointerTargetSelector?: string
  /** Called with `visible && !document.hidden` whenever visibility changes. */
  onVisibilityChange?: (active: boolean) => void
  /** When false, the shape shell divs are still created (the Three.js
   *  renderers measure them) but no dot spans or animation loop run —
   *  WebGL clients get their dots from Three.js instead. Defaults true. */
  enabled?: boolean
}

const home = { x: 0, y: 0, vx: 0, vy: 0 }

/**
 * Reusable dither repulsion animation with cone/wake mouse interaction.
 *
 * Creates dithered dot elements from tone maps, tracks mouse movement with
 * smoothed velocity (weight/inertia), and pushes dots in a cone-shaped wake
 * behind the cursor. Dots spring back to their original positions after the
 * mouse passes or stops. Animation is deferred until near the viewport, sleeps
 * when settled, and is disabled for reduced-motion preferences.
 */
export function useDitherRepulsion(
  rootRef: React.RefObject<HTMLElement | null>,
  artRef: React.RefObject<HTMLElement | null>,
  options: DitherRepulsionOptions,
) {
  const { shapes, radius = 100, maxPush = 70, pointerTargetSelector, enabled = true } = options
  const onVisibilityChangeRef = useRef(options.onVisibilityChange)
  useEffect(() => {
    onVisibilityChangeRef.current = options.onVisibilityChange
  })

  useEffect(() => {
    const root = rootRef.current
    const art = artRef.current
    if (!root || !art) return

    const pointerTarget = pointerTargetSelector
      ? (root.closest(pointerTargetSelector) as HTMLElement | null) ?? root
      : root

    const motion = window.matchMedia('(prefers-reduced-motion: reduce)')
    const groups: { element: HTMLDivElement; width: number; height: number; dots: Particle[] }[] =
      []
    const active = new Set<Particle>()
    let particles: Particle[] = []
    let pointer: { x: number; y: number } | null = null
    let lastPointer: { x: number; y: number } | null = null
    let vel = { x: 0, y: 0 }
    let visible = false
    let initialized = false
    let layoutDirty = true
    let frame = 0
    let lastTime = 0
    let entranceStartTime = 0
    let bounds = root.getBoundingClientRect()
    // Smooth ease-out: gentle deceleration without the aggressive shoot-then-crawl.
    const entranceEase = cubicBezier(0.16, 1, 0.3, 1)

    function syncVisibility() {
      onVisibilityChangeRef.current?.(visible && !document.hidden)
    }

    function initialize() {
      if (initialized) return
      initialized = true
      const fragment = document.createDocumentFragment()
      for (const shape of shapes) {
        const element = document.createElement('div')
        element.className = shape.className
        if (shape.fillContainer) {
          element.style.width = '100%'
          element.style.height = '100%'
        } else {
          element.style.aspectRatio = `${shape.tone.width} / ${shape.tone.height}`
        }
        element.style.containerType = 'inline-size'
        const dots: Particle[] = enabled
          ? ditherClusters(shape.tone).map(({ x, y, offsets }) => {
              const dot = document.createElement('span')
              const scale = shape.dotScale ?? 1
              dot.style.fontSize = `${(100 / shape.tone.width) * scale}cqw`
              dot.style.boxShadow = offsets
                .map((offset) => `${offset.x}em ${offset.y}em 0 currentColor`)
                .join(',')
              dot.style.left = `${(x / shape.tone.width) * 100}%`
              dot.style.top = `${(y / shape.tone.height) * 100}%`
              dot.style.width = `${(90 / shape.tone.width) * scale}%`
              dot.style.height = `${(90 / shape.tone.height) * scale}%`
              if (shape.hidden) dot.style.opacity = '0'
              element.appendChild(dot)
              // Start dots at random scattered positions for the entrance animation.
              const useEntrance = shape.entrance ?? false
              const entranceStart = useEntrance
                ? {
                    x: (Math.random() - 0.5) * 300,
                    y: (Math.random() - 0.5) * 200,
                  }
                : home
              if (entranceStart.x || entranceStart.y) {
                dot.style.transform = `translate(${entranceStart.x.toFixed(2)}px, ${entranceStart.y.toFixed(2)}px)`
                // Start invisible — opacity is driven by entrance progress.
              }
              return {
                element: dot,
                column: x,
                row: y,
                x: 0,
                y: 0,
                target: home,
                state: { x: entranceStart.x, y: entranceStart.y, vx: 0, vy: 0 },
                hidden: shape.hidden ?? false,
                entrance: useEntrance,
                entranceStart,
                entranceDelay: useEntrance ? Math.random() * 0.2 : 0,
                entranceDuration: 1.0 + Math.random() * 0.3,
              }
            })
          : []
        groups.push({ element, width: shape.tone.width, height: shape.tone.height, dots })
        fragment.appendChild(element)
      }
      particles = groups.flatMap((group) => group.dots)
      art!.appendChild(fragment)
      // Add entrance-scattered dots to the active set so the
      // animation kicks off immediately.
      for (const particle of particles) {
        if (particle.state.x || particle.state.y) active.add(particle)
      }
      if (active.size) {
        entranceStartTime = performance.now()
        schedule()
      }
    }

    function reset() {
      cancelAnimationFrame(frame)
      frame = 0
      lastTime = 0
      entranceStartTime = 0
      pointer = null
      lastPointer = null
      vel = { x: 0, y: 0 }
      for (const particle of particles) {
        if (particle.state !== home) particle.element.style.transform = ''
        if (particle.hidden) particle.element.style.opacity = '0'
        particle.state = home
        particle.target = home
      }
      active.clear()
    }

    function measure() {
      bounds = root!.getBoundingClientRect()
      for (const group of groups) {
        const box = group.element.getBoundingClientRect()
        for (const dot of group.dots) {
          dot.x = box.left - bounds.left + ((dot.column + 0.45) / group.width) * box.width
          dot.y = box.top - bounds.top + ((dot.row + 0.45) / group.height) * box.height
        }
      }
      layoutDirty = false
    }

    function tick(now: number) {
      frame = 0
      if (layoutDirty) measure()

      // Decay velocity each frame so dots spring back after the mouse passes.
      vel.x *= 0.9
      vel.y *= 0.9
      const speed = Math.hypot(vel.x, vel.y)

      // Recalculate targets every frame the tick is running. This ensures
      // dots spring back to home after pointer leave, resize, or velocity decay.
      {
        const px = pointer ? pointer.x - bounds.left : 0
        const py = pointer ? pointer.y - bounds.top : 0

        for (const particle of particles) {
          const dx = particle.x - px
          const dy = particle.y - py
          const distance = Math.hypot(dx, dy)

          if (pointer && distance < radius) {
            const falloff = 1 - distance / radius

            if (speed > 0.5) {
              // Cone / wake: push dots to the sides of the mouse path.
              const dirX = vel.x / speed
              const dirY = vel.y / speed
              const perpX = -dirY
              const perpY = dirX
              const parallel = dx * dirX + dy * dirY
              const perp = dx * perpX + dy * perpY
              const sideSign = perp >= 0 ? 1 : -1
              const coneFactor = parallel < 0 ? 1 + (Math.abs(parallel) / radius) * 0.6 : 0.4
              const pushMag = falloff * maxPush * Math.min(speed / 8, 1.8) * coneFactor
              const target = {
                x: perpX * sideSign * pushMag + dirX * pushMag * 0.15,
                y: perpY * sideSign * pushMag + dirY * pushMag * 0.15,
              }
              if (target.x !== particle.target.x || target.y !== particle.target.y) {
                particle.target = target
                active.add(particle)
              }
            } else {
              // Mouse is slow or stationary — gentle radial push.
              const radialX = distance > 0.1 ? (dx / distance) * falloff * maxPush * 0.25 : 0
              const radialY = distance > 0.1 ? (dy / distance) * falloff * maxPush * 0.25 : 0
              if (radialX !== particle.target.x || radialY !== particle.target.y) {
                particle.target = { x: radialX, y: radialY }
                active.add(particle)
              }
            }
          } else if (particle.target.x !== 0 || particle.target.y !== 0) {
            particle.target = home
            active.add(particle)
          }
        }
      }

      const delta = lastTime ? (now - lastTime) / 1000 : 1 / 60
      lastTime = now
      const entranceElapsed = entranceStartTime ? (now - entranceStartTime) / 1000 : 0
      for (const particle of active) {
        if (particle.entrance && entranceStartTime) {
          // Bezier-eased entrance from scattered position to home.
          const t = Math.max(
            0,
            Math.min(1, (entranceElapsed - particle.entranceDelay) / particle.entranceDuration),
          )
          if (t >= 1) {
            particle.state = home
            particle.entrance = false
            particle.element.style.transform = ''
            if (particle.hidden) particle.element.style.opacity = '0'
            active.delete(particle)
            continue
          }
          const eased = entranceEase(t)
          const x = particle.entranceStart.x * (1 - eased)
          const y = particle.entranceStart.y * (1 - eased)
          particle.state = { x, y, vx: 0, vy: 0 }
          particle.element.style.transform = `translate(${x.toFixed(2)}px, ${y.toFixed(2)}px)`
          if (particle.hidden) {
            // Fade in during first 20%, hold, fade out during last 30%.
            let opacity: number
            if (t < 0.2) opacity = t / 0.2
            else if (t > 0.7) opacity = (1 - t) / 0.3
            else opacity = 1
            particle.element.style.opacity = opacity.toFixed(3)
          }
          continue
        }

        const state = springStep(particle.state, particle.target, delta)
        particle.state = state
        particle.element.style.transform =
          state.x || state.y ? `translate(${state.x.toFixed(2)}px, ${state.y.toFixed(2)}px)` : ''
        if (particle.hidden) {
          const displacement = Math.hypot(state.x, state.y)
          particle.element.style.opacity = Math.min(displacement / 8, 1).toFixed(3)
        }
        if (
          state.x === particle.target.x &&
          state.y === particle.target.y &&
          !state.vx &&
          !state.vy
        ) {
          if (particle.hidden) particle.element.style.opacity = '0'
          active.delete(particle)
        }
      }

      // Keep ticking while particles are moving or velocity is decaying.
      if (active.size || speed > 0.3) frame = requestAnimationFrame(tick)
      else lastTime = 0
    }

    function schedule() {
      if (!frame && visible && !motion.matches && !document.hidden)
        frame = requestAnimationFrame(tick)
    }

    function move(event: PointerEvent) {
      if (event.pointerType === 'touch' || motion.matches || !visible) return
      const current = { x: event.clientX, y: event.clientY }
      if (lastPointer) {
        const rawVx = current.x - lastPointer.x
        const rawVy = current.y - lastPointer.y
        vel.x = vel.x * 0.5 + rawVx * 0.5
        vel.y = vel.y * 0.5 + rawVy * 0.5
      }
      lastPointer = current
      pointer = current
      schedule()
    }

    function leave() {
      pointer = null
      lastPointer = null
      schedule()
    }

    function invalidate() {
      layoutDirty = true
      leave()
    }

    function visibilityChange() {
      syncVisibility()
      if (document.hidden) reset()
      else layoutDirty = true
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting
        syncVisibility()
        if (visible) {
          initialize()
          layoutDirty = true
        } else reset()
      },
      { rootMargin: '160px 0px' },
    )
    const resize = new ResizeObserver(invalidate)
    observer.observe(root)
    resize.observe(root)
    pointerTarget.addEventListener('pointermove', move, { passive: true })
    pointerTarget.addEventListener('pointerleave', leave)
    pointerTarget.addEventListener('pointercancel', leave)
    window.addEventListener('scroll', invalidate, { passive: true })
    window.addEventListener('blur', reset)
    document.addEventListener('visibilitychange', visibilityChange)
    motion.addEventListener('change', reset)

    return () => {
      reset()
      observer.disconnect()
      resize.disconnect()
      pointerTarget.removeEventListener('pointermove', move)
      pointerTarget.removeEventListener('pointerleave', leave)
      pointerTarget.removeEventListener('pointercancel', leave)
      window.removeEventListener('scroll', invalidate)
      window.removeEventListener('blur', reset)
      document.removeEventListener('visibilitychange', visibilityChange)
      motion.removeEventListener('change', reset)
      art.replaceChildren()
    }
  }, [shapes, radius, maxPush, pointerTargetSelector, enabled])
}
